import React, { useCallback, useMemo } from 'react';
import {
  AppButton,
  Box,
  CardTypesEnum,
  ContributionsMenuIcon,
  DividerIcon,
  Hint,
  InfoOutlineIcon,
  Inscription,
  Text,
  WarningCard,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { ServiceCategoryTypes } from '@/modules/employer/types/ServiceCategories.types';
import useCopaysFormValidate from '@/modules/Renewals/RenewalSetup/CopaysStep/hooks/useCopaysFormValidate.ts';
import theme from '@/styles/theme';

import { Renewal } from '../../renewals.types';
import { RENEWAL_SETUP_CONTENT_WIDTH } from '../RenewalSetup';
import { CopaysOptionValues, RenewalSetupStep } from '../RenewalSetup.types';
import RenewalSetupLinks from '../RenewalSetupLinks';
import { useRenewalSetupStore } from '../stores';
import { useCopayStore } from '../stores/useCopay.store';
import { getStepLabel } from '../utils';

import ChangeCopaysForm from './CopaysForm/CopaysChangeForm';
import CopaysForm from './CopaysForm/CopaysForm';
import CanceledItem from './CanceledItem';
import CopaysSummary from './CopaysSummary';
import { CopaysSummaryContainer } from './CopaysSummaryContainer';

interface Props {
  steps: RenewalSetupStep[];
  renewalSetupData?: Renewal;
  serviceCategories?: ServiceCategoryTypes[];
  handleSetVisibleRenewPopup: (value: boolean) => void;
}

const CopaysStep: React.FC<Props> = ({
  steps,
  renewalSetupData,
  serviceCategories,
  handleSetVisibleRenewPopup,
}) => {
  const copaysSelectedOption = useRenewalSetupStore((state) => state.copays.selectedOption);
  const isOptionChosen = useRenewalSetupStore((state) => state.copays.isOptionChosen);
  const setStepState = useRenewalSetupStore((state) => state.setStepState);
  const isPlansStepFailed = useRenewalSetupStore((state) => state.plansPlanYear.isFailed);
  const isHealthPlansStepFailed = useRenewalSetupStore((state) => state.healthPlans.isFailed);
  const isRevalidationPending = useRenewalSetupStore((state) => state.isRevalidationPending);
  const copayErrors = useCopaysFormValidate();
  const handleShowErrorsCopays = useCopayStore((state) => state.setShowErrors);
  const hasCopays = useMemo(() => !!renewalSetupData?.copays?.length, [renewalSetupData?.copays]);
  const hasFailedStep = isPlansStepFailed || isHealthPlansStepFailed || isRevalidationPending;

  const handleSubmit = useCallback(async () => {
    handleShowErrorsCopays(true);
    if (
      (copaysSelectedOption === CopaysOptionValues.REQUESTED && _.isEmpty(copayErrors)) ||
      copaysSelectedOption !== CopaysOptionValues.REQUESTED
    ) {
      handleSetVisibleRenewPopup(true);
      handleShowErrorsCopays(false);
    }
  }, [handleShowErrorsCopays, copaysSelectedOption, copayErrors, handleSetVisibleRenewPopup]);

  return (
    <Box style={{ maxWidth: RENEWAL_SETUP_CONTENT_WIDTH }} data-testid="copays-step">
      <Box margin={{ bottom: 'xs' }}>
        <ContributionsMenuIcon size="48px" color={theme.colors.cardTypes[CardTypesEnum.TRAVEL]} />
      </Box>
      <Inscription weight="bold" size="32px" lineHeight="44px" color="textTitle" margin={{ bottom: 'xs' }}>
        Renew Copays?
      </Inscription>
      <Text color="textBody" margin={{ bottom: 'l' }}>
        Your current plan year ends on{' '}
        <Text as="span">{dayjs(renewalSetupData?.planYear?.validTo).format(DEFAULT_DATE_FORMAT)}</Text>.
        Renewing plans will be from{' '}
        <Text as="span" weight="bold">
          {dayjs(renewalSetupData?.newPlanYear?.validFrom).format(DEFAULT_DATE_FORMAT)}
        </Text>{' '}
        to{' '}
        <Text as="span" weight="bold">
          {dayjs(renewalSetupData?.newPlanYear?.validTo).format(DEFAULT_DATE_FORMAT)}
        </Text>
        .
      </Text>
      {!hasCopays && (
        <WarningCard
          testId="no-copays-info"
          icon={<InfoOutlineIcon color="info" />}
          title={
            <Inscription size="14px" lineHeight="20px" color="textBody">
              Your organization didn&apos;t use copays last year. You can add them for the upcoming year now,
              or skip this step if they aren&apos;t needed.
            </Inscription>
          }
          backgroundColor="infoContainer"
          borderColor="infoBorder"
          topBorderColor="info"
          containerProps={{
            pad: { horizontal: 'spacing24', vertical: 'spacing16' },
            round: 'container1Round',
          }}
          margin={{ bottom: 'spacing24' }}
        />
      )}
      {hasCopays && <CopaysSummary renewalSetupData={renewalSetupData} />}
      <CopaysForm
        copaysSelectedOption={copaysSelectedOption}
        handleSetStepState={setStepState}
        hasCopays={hasCopays}
      />
      {isOptionChosen ? null : (
        <AppButton
          disabled={!copaysSelectedOption}
          onClick={() => setStepState(RenewalSetupStep.Copays, { isOptionChosen: true })}
          testId="choose-option-button"
          style={{ marginTop: theme.spacings.l, height: '54px' }}
          containerStyle={{ maxWidth: 'none', width: '100%' }}
        >
          Choose Option
        </AppButton>
      )}
      {isOptionChosen && (
        <>
          <Box margin={{ vertical: 'l' }}>
            <DividerIcon width="100%" />
          </Box>
          <Inscription weight="bold" size="32px" lineHeight="44px" color="textTitle" margin={{ bottom: 'l' }}>
            {!hasCopays && copaysSelectedOption === CopaysOptionValues.REQUESTED
              ? 'Create Copay(s)'
              : 'Summary of Your Copays'}
          </Inscription>
          {copaysSelectedOption === CopaysOptionValues.ACTIVE && (
            <Box background="module" pad="l" round="container1Round">
              <CopaysSummaryContainer copays={renewalSetupData?.copays} />
            </Box>
          )}
          {copaysSelectedOption === CopaysOptionValues.REQUESTED && (
            <Box background="module" round="container1Round" pad="l">
              <ChangeCopaysForm serviceCategories={serviceCategories} />
            </Box>
          )}
          {copaysSelectedOption === CopaysOptionValues.CANCELED && (
            <Box background="dangerContainer" pad="l" round="container1Round">
              <CanceledItem planYear={renewalSetupData?.newPlanYear} />
            </Box>
          )}
          <Inscription color="textBody" weight="bold" textAlign="center" margin={{ top: 'l', bottom: 's' }}>
            Look good? If so, proceed to the next step!
          </Inscription>
          {hasFailedStep ? (
            <Hint
              hintAlign="vertical"
              wrapperWidth="100%"
              dropProps={{
                stretch: false,
              }}
              hintWidth={200}
              hintElement={
                <AppButton
                  onClick={handleSubmit}
                  disabled
                  testId="open-renew-popup"
                  style={{ width: '100%', maxWidth: 'none', height: '54px' }}
                  containerStyle={{ maxWidth: 'none', width: '100%' }}
                >
                  {getStepLabel(steps, RenewalSetupStep.Copays)}
                </AppButton>
              }
            >
              <Inscription size="small" textAlign="center">
                Please resolve all issues in previous steps before submitting your renewal.
              </Inscription>
            </Hint>
          ) : (
            <AppButton
              onClick={handleSubmit}
              testId="open-renew-popup"
              style={{ width: '100%', maxWidth: 'none', height: '54px' }}
              containerStyle={{ maxWidth: 'none', width: '100%' }}
            >
              {getStepLabel(steps, RenewalSetupStep.Copays)}
            </AppButton>
          )}
        </>
      )}
      <RenewalSetupLinks renewalSetupData={renewalSetupData} />
    </Box>
  );
};

export default CopaysStep;
